// apps/admin/src/components/EmlUploadSection.tsx
// EML file upload UI for admin portal email ingestion

import React, { useState, DragEvent } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../firebaseConfig';
import { Mail, Upload, FileText, X, CheckCircle, AlertTriangle } from 'lucide-react';

interface EmlUploadSectionProps {
  companyId: string;
  offices: Array<{ id: string; name: string }>;
  onUploadSuccess?: () => void;
}

interface EmlFile {
  file: File;
  id: string; // unique key for list rendering
}

const EmlUploadSection: React.FC<EmlUploadSectionProps> = ({
  companyId,
  offices,
  onUploadSuccess
}) => {
  const [selectedOfficeId, setSelectedOfficeId] = useState<string>(offices[0]?.id || '');
  const [emlFiles, setEmlFiles] = useState<EmlFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    addFiles(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      addFiles(files);
    }
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const addFiles = (files: FileList) => {
    const validFiles: EmlFile[] = [];
    const invalidNames: string[] = [];

    Array.from(files).forEach(file => {
      if (file.name.toLowerCase().endsWith('.eml')) {
        validFiles.push({
          file,
          id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`
        });
      } else {
        invalidNames.push(file.name);
      }
    });

    if (invalidNames.length > 0) {
      setError(`Invalid file type(s): ${invalidNames.join(', ')}. Only .eml files are accepted.`);
    } else {
      setError(null);
    }

    if (validFiles.length > 0) {
      setEmlFiles(prev => [...prev, ...validFiles]);
      setSuccessMessage(null);
    }
  };

  const removeFile = (id: string) => {
    setEmlFiles(prev => prev.filter(f => f.id !== id));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:message/rfc822;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
      reader.readAsDataURL(file);
    });
  };

  const handleUpload = async () => {
    if (emlFiles.length === 0) {
      setError('Please select at least one .eml file to upload.');
      return;
    }
    if (!selectedOfficeId) {
      setError('Please select an office.');
      return;
    }

    // Check total size (10MB limit for callable payload)
    const totalSize = emlFiles.reduce((sum, f) => sum + f.file.size, 0);
    if (totalSize > 10 * 1024 * 1024) {
      setError('Total file size cannot exceed 10MB for email uploads.');
      return;
    }

    setLoading('Preparing email files...');
    setError(null);
    setSuccessMessage(null);
    setUploadProgress(0);

    try {
      // Verify authentication and refresh token
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError('Authentication required. Please refresh the page and log in again.');
        setLoading(null);
        return;
      }

      try {
        const tokenResult = await currentUser.getIdTokenResult(true);
        const hasFounderRole = tokenResult.claims.isFounder === true ||
                              tokenResult.claims.role === 'founder';
        if (!hasFounderRole) {
          setError('Insufficient permissions. Only founders can upload email files.');
          setLoading(null);
          return;
        }
      } catch (tokenError) {
        console.error('[EmlUpload] Token refresh failed:', tokenError);
        setError('Authentication token refresh failed. Please refresh the page and try again.');
        setLoading(null);
        return;
      }

      // Read all files as base64
      setLoading(`Reading ${emlFiles.length} file(s)...`);
      const emlPayload: Array<{ fileName: string; base64: string }> = [];

      for (let i = 0; i < emlFiles.length; i++) {
        const emlFile = emlFiles[i];
        setUploadProgress(Math.round(((i) / emlFiles.length) * 50));
        const base64 = await readFileAsBase64(emlFile.file);
        emlPayload.push({
          fileName: emlFile.file.name,
          base64
        });
      }

      setUploadProgress(50);
      setLoading('Uploading email files for processing...');

      const uploadSupplementalData = httpsCallable(functions, 'uploadSupplementalData');
      await uploadSupplementalData({
        companyId,
        officeId: selectedOfficeId,
        uploadMode: 'emlUpload',
        emlFiles: emlPayload
      });

      setUploadProgress(100);
      setLoading(null);
      setSuccessMessage(`Successfully uploaded ${emlFiles.length} email file(s) for processing.`);
      setEmlFiles([]);

      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (err: any) {
      setError(`Upload failed: ${err.message}`);
      setLoading(null);
    }
  };

  const inputClasses = "w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg placeholder-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-fuchsia-500";
  const buttonClasses = "w-full py-3 font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-all";

  return (
    <div className="p-6 rounded-2xl shadow-xl bg-black/20 backdrop-blur-lg border border-white/10">
      <h3 className="text-xl font-semibold mb-1 flex items-center gap-2">
        <Mail className="w-5 h-5 text-indigo-400" />
        Email Upload
      </h3>
      <p className="text-slate-400 text-sm mb-6">Upload .eml files for commission data extraction</p>

      {error && (
        <div className="p-3 my-4 bg-red-500/20 border border-red-500/30 rounded-md flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="p-3 my-4 bg-green-500/20 border border-green-500/30 rounded-md flex items-start gap-2">
          <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-green-400">{successMessage}</p>
        </div>
      )}

      {/* Office Selector */}
      <div className="mb-6">
        <label htmlFor="eml-office" className="block text-sm font-medium text-slate-300 mb-1">
          Office
        </label>
        <select
          id="eml-office"
          value={selectedOfficeId}
          onChange={e => setSelectedOfficeId(e.target.value)}
          className={inputClasses}
          disabled={offices.length === 0}
        >
          {offices.length === 0 && <option>Loading offices...</option>}
          {offices.map(office => (
            <option key={office.id} value={office.id}>{office.name}</option>
          ))}
        </select>
      </div>

      {/* Info Panel */}
      <div className="mb-6 bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg">
        <h4 className="font-semibold mb-2 text-blue-300 flex items-center gap-2">
          <Mail className="w-4 h-4" />
          Email Upload Guidelines
        </h4>
        <ul className="space-y-1 text-sm text-slate-300">
          <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" /> Upload .eml files exported from email clients</li>
          <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" /> Multiple files can be uploaded at once</li>
          <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" /> Attachments are extracted and processed automatically</li>
          <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" /> Max total size: 10MB per upload batch</li>
        </ul>
      </div>

      {/* EML Dropzone */}
      <div className="space-y-4">
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`flex justify-center px-6 pt-8 pb-8 border-2 border-slate-700 border-dashed rounded-md transition-colors ${isDragging ? 'bg-slate-800/50 border-fuchsia-500' : ''}`}
        >
          <div className="space-y-1 text-center">
            <Mail className="w-12 h-12 mx-auto text-slate-500 mb-4" />
            <div className="flex text-lg text-slate-300 font-medium justify-center">
              <label htmlFor="eml-fileinput" className="relative cursor-pointer bg-slate-900 rounded-md font-medium text-indigo-400 hover:text-indigo-300">
                <span>{emlFiles.length > 0 ? `${emlFiles.length} file(s) selected` : 'Click to upload .eml files'}</span>
                <input
                  id="eml-fileinput"
                  type="file"
                  accept=".eml"
                  multiple
                  onChange={handleFileSelect}
                  className="sr-only"
                />
              </label>
            </div>
            <p className="text-sm text-slate-500">or drag and drop (max 10MB total)</p>
          </div>
        </div>

        {/* File List Preview */}
        {emlFiles.length > 0 && (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between">
              <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Selected Files ({emlFiles.length})
              </h4>
              <button
                onClick={() => setEmlFiles([])}
                className="text-xs text-slate-500 hover:text-red-400 transition-colors"
              >
                Clear all
              </button>
            </div>
            <ul className="divide-y divide-slate-700/30">
              {emlFiles.map(emlFile => (
                <li key={emlFile.id} className="px-4 py-3 flex items-center justify-between hover:bg-slate-700/20 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <Mail className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                    <span className="text-sm text-white truncate">{emlFile.file.name}</span>
                    <span className="text-xs text-slate-500 flex-shrink-0">{formatFileSize(emlFile.file.size)}</span>
                  </div>
                  <button
                    onClick={() => removeFile(emlFile.id)}
                    className="text-slate-500 hover:text-red-400 transition-colors flex-shrink-0 ml-2"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="px-4 py-2 border-t border-slate-700/50 text-xs text-slate-500">
              Total: {formatFileSize(emlFiles.reduce((sum, f) => sum + f.file.size, 0))}
            </div>
          </div>
        )}
      </div>

      {/* Progress */}
      {loading && (
        <div className="mt-4">
          <p className="text-center text-indigo-400 mb-2">{loading}</p>
          {uploadProgress > 0 && (
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div
                className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Upload Button */}
      <div className="mt-6">
        <button
          onClick={handleUpload}
          disabled={!!loading || emlFiles.length === 0}
          className={buttonClasses}
        >
          <span className="flex items-center justify-center gap-2">
            <Upload className="w-4 h-4" />
            {loading ? 'Uploading...' : `Upload ${emlFiles.length > 0 ? emlFiles.length : ''} Email File${emlFiles.length !== 1 ? 's' : ''}`}
          </span>
        </button>
      </div>
    </div>
  );
};

export default EmlUploadSection;
